/*
 * ProfileModal v2 — QR menü müşteri profil modal'ı.
 *
 * Akış (CustomerAuthContext authoritative):
 *   1. 'auth'        → Anonim. Google butonu + AZ telefon (+994) input.
 *   2. 'otp'         → 6 haneli kod input + countdown + resend.
 *   3. 'profile'     → Giriş yapmış. Avatar, ad, e-posta, telefon görünür.
 *                      Profili düzenle / Verdiğim qiymətlər / Çıxış.
 *   4. 'edit'        → name + email + birth_date formu (PATCH /me).
 *   5. 'ratings'     → GET /customer/ratings/mine listesi.
 *
 * Tasarım hedefi: native iOS feel, küçük spring animasyonlar, hızlı,
 * react-toastify yerine Dynamic Island ile bildirim.
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  X,
  Star,
  Phone,
  ArrowRight,
  ArrowLeft,
  User,
  Settings,
  CheckCircle2,
  Moon,
  Sun,
  LogOut,
  Mail,
  Calendar,
  Heart,
  Pencil,
  ChevronRight,
  Loader2,
  Gift,
  ShieldCheck,
  RefreshCw,
} from 'lucide-react';
import { useBodyScrollLock } from './useBodyScrollLock';
import { useQrLanguage } from './LanguageContext';
import { useDynamicIsland } from './DynamicIsland';
import { useCustomerAuth } from '../../context/CustomerAuthContext';
import { useCustomerFavorites } from '../../context/CustomerFavoritesContext';
import {
  AZ_PHONE_PREFIX,
  buildAzPhone,
  isValidAzLocal,
  sanitizeAzLocalDigits,
} from '../../api/customerAuth';
import {
  CustomerRating,
  getMyRatings,
} from '../../api/customerRatings';

// ---------------------------------------------------------------------------
// Backward-compat — eski OrderDetailsQrcodV2 hâlâ bu tipi referans alabilir.
// Yeni implementasyon CustomerAuthContext'in CustomerUser tipini kullanıyor.
// ---------------------------------------------------------------------------
export interface QrUserProfile {
  id?: string | number;
  name: string;
  email?: string;
  picture?: string;
  provider: 'google' | 'apple' | 'phone';
  givenName?: string;
  familyName?: string;
  phone?: string;
  token?: string;
}

interface ProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  masaName?: string;
  theme?: 'light' | 'dark';
  onToggleTheme?: () => void;
  /** @deprecated Auth state artık CustomerAuthContext'ten okunuyor. */
  user?: QrUserProfile | null;
  /** @deprecated CustomerAuthContext kullanılıyor. */
  onUserChange?: (user: QrUserProfile | null) => void;
  /** Google Identity Services client ID. Env: REACT_APP_GOOGLE_CLIENT_ID. */
  googleClientId?: string;
  /** @deprecated Apple sign-in artık desteklenmiyor. */
  appleServiceId?: string;
}

declare global {
  interface Window {
    google?: any;
  }
}

// ---------------------------------------------------------------------------
// Utility: Google SDK script loader
// ---------------------------------------------------------------------------

const loadScript = (src: string, id: string) =>
  new Promise<void>((resolve, reject) => {
    if (document.getElementById(id)) return resolve();
    const s = document.createElement('script');
    s.src = src;
    s.async = true;
    s.defer = true;
    s.id = id;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(s);
  });

const haptic = (ms = 10) => {
  if (typeof window !== 'undefined' && window.navigator && window.navigator.vibrate) {
    window.navigator.vibrate(ms);
  }
};

const initialOf = (name?: string | null): string =>
  (name || 'U').trim().charAt(0).toUpperCase();

const formatTime = (sec: number): string => {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
};

// ---------------------------------------------------------------------------
// View modes
// ---------------------------------------------------------------------------

type ViewMode = 'auth' | 'otp' | 'profile' | 'edit' | 'ratings';

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function ProfileModal({
  isOpen,
  onClose,
  masaName,
  theme = 'light',
  onToggleTheme,
  googleClientId,
}: ProfileModalProps) {
  const { t } = useQrLanguage();
  const island = useDynamicIsland();
  const auth = useCustomerAuth();
  const favorites = useCustomerFavorites();

  // ---------- View state ----------
  const [view, setView] = useState<ViewMode>(() =>
    auth.isAuthenticated ? 'profile' : 'auth',
  );

  // ---------- Auth flow state ----------
  const [phoneLocal, setPhoneLocal] = useState('');
  const [otpDigits, setOtpDigits] = useState<string[]>(['', '', '', '', '', '']);
  const [otpCooldown, setOtpCooldown] = useState(0);
  const [otpExpires, setOtpExpires] = useState(0);
  const [otpName, setOtpName] = useState('');
  const [needsName, setNeedsName] = useState(false);
  const [authBusy, setAuthBusy] = useState<'google' | 'phone' | null>(null);

  // ---------- Edit form ----------
  const [editForm, setEditForm] = useState({
    name: '',
    email: '',
    birth_date: '',
  });
  const [editBusy, setEditBusy] = useState(false);

  // ---------- Ratings ----------
  const [ratings, setRatings] = useState<CustomerRating[]>([]);
  const [ratingsLoading, setRatingsLoading] = useState(false);

  // ---------- Refs ----------
  const googleBtnRef = useRef<HTMLDivElement>(null);
  const googleInitRef = useRef(false);
  const otpInputsRef = useRef<HTMLInputElement[]>([]);

  useBodyScrollLock(isOpen);

  // Effective Google client ID (props OR env).
  const GOOGLE_CLIENT_ID =
    googleClientId ||
    (typeof process !== 'undefined' ? process.env.REACT_APP_GOOGLE_CLIENT_ID : '') ||
    '';

  // ---------- isAuth değişimine view senkronizasyonu ----------
  useEffect(() => {
    if (!isOpen) return;
    if (auth.isAuthenticated && (view === 'auth' || view === 'otp')) {
      setView('profile');
    } else if (
      !auth.isAuthenticated &&
      (view === 'profile' || view === 'edit' || view === 'ratings')
    ) {
      setView('auth');
    }
  }, [auth.isAuthenticated, isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Modal her açıldığında stale state'leri temizle.
  useEffect(() => {
    if (isOpen) {
      setView(auth.isAuthenticated ? 'profile' : 'auth');
      setPhoneLocal('');
      setOtpDigits(['', '', '', '', '', '']);
      setOtpCooldown(0);
      setOtpExpires(0);
      setOtpName('');
      setNeedsName(false);
      setAuthBusy(null);
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------- Edit form sync ----------
  useEffect(() => {
    if (auth.customer) {
      setEditForm({
        name: auth.customer.name || '',
        email: auth.customer.email || '',
        birth_date: auth.customer.birth_date || '',
      });
    }
  }, [auth.customer]);

  // ---------- OTP cooldown countdown ----------
  useEffect(() => {
    if (otpCooldown <= 0) return;
    const id = setInterval(() => {
      setOtpCooldown((s) => Math.max(0, s - 1));
    }, 1000);
    return () => clearInterval(id);
  }, [otpCooldown]);

  // ---------- OTP expires countdown ----------
  useEffect(() => {
    if (view !== 'otp' || otpExpires <= 0) return;
    const id = setInterval(() => {
      setOtpExpires((s) => Math.max(0, s - 1));
    }, 1000);
    return () => clearInterval(id);
  }, [view, otpExpires]);

  // ---------- Ratings yüklemesi ----------
  useEffect(() => {
    if (view !== 'ratings' || !auth.isAuthenticated) return;
    const ac = new AbortController();
    let cancelled = false;
    setRatingsLoading(true);
    getMyRatings(ac.signal)
      .then((list) => {
        if (!cancelled) setRatings(list);
      })
      .catch(() => {
        /* sessizce geç — UI boş state gösterir */
      })
      .finally(() => {
        if (!cancelled) setRatingsLoading(false);
      });
    return () => {
      cancelled = true;
      ac.abort();
    };
  }, [view, auth.isAuthenticated]);

  // ---------- Google Sign-In init ----------
  useEffect(() => {
    if (!isOpen || auth.isAuthenticated || googleInitRef.current || !GOOGLE_CLIENT_ID) {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        await loadScript('https://accounts.google.com/gsi/client', 'google-gsi-script');
        if (cancelled || !window.google?.accounts?.id) return;
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: async (response: any) => {
            if (!response?.credential) return;
            await handleGoogleCredential(response.credential);
          },
          auto_select: false,
          cancel_on_tap_outside: true,
          ux_mode: 'popup',
        });
        googleInitRef.current = true;
      } catch {
        /* SDK yüklenemedi — phone akışı yine çalışır */
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, auth.isAuthenticated, GOOGLE_CLIENT_ID]);

  // ---------- Google: ID token → backend exchange ----------
  const handleGoogleCredential = useCallback(
    async (idToken: string) => {
      setAuthBusy('google');
      try {
        const customer = await auth.signInWithGoogle(idToken);
        island.show({
          type: 'success',
          title: customer.name
            ? `${(t as any).welcomeBack ? (t as any).welcomeBack(customer.name) : `Xoş gəldin, ${customer.name}`}`
            : (t as any).loggedInAsGoogle || 'Daxil oldunuz',
        });
      } catch (err: any) {
        island.show({
          type: 'error',
          title:
            err?.response?.data?.message ||
            err?.message ||
            'Google ilə daxil ola bilmədik',
        });
      } finally {
        setAuthBusy(null);
      }
    },
    [auth, island, t],
  );

  // ---------- Google button (popup fallback) ----------
  const handleGoogleLogin = useCallback(async () => {
    if (!GOOGLE_CLIENT_ID) {
      island.show({
        type: 'warn',
        title: 'Google Client ID təyin edilməyib',
        message: 'REACT_APP_GOOGLE_CLIENT_ID',
      });
      return;
    }
    haptic(8);
    try {
      await loadScript('https://accounts.google.com/gsi/client', 'google-gsi-script');
      if (!window.google?.accounts?.id) {
        throw new Error('Google SDK yüklənmədi');
      }
      if (!googleInitRef.current) {
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: async (response: any) => {
            if (!response?.credential) return;
            await handleGoogleCredential(response.credential);
          },
          auto_select: false,
          ux_mode: 'popup',
        });
        googleInitRef.current = true;
      }
      // One-tap iste, başarısızsa popup butonuna fallback.
      window.google.accounts.id.prompt((notification: any) => {
        if (notification.isNotDisplayed?.() || notification.isSkippedMoment?.()) {
          if (googleBtnRef.current) {
            googleBtnRef.current.innerHTML = '';
            window.google.accounts.id.renderButton(googleBtnRef.current, {
              type: 'standard',
              theme: 'outline',
              size: 'large',
              text: 'continue_with',
              width: 320,
            });
            setTimeout(() => {
              const btn =
                googleBtnRef.current?.querySelector<HTMLDivElement>('div[role="button"]');
              if (btn) btn.click();
            }, 100);
          }
        }
      });
    } catch (e: any) {
      island.show({
        type: 'error',
        title: 'Google ilə daxil olma alınmadı',
        message: e?.message,
      });
    }
  }, [GOOGLE_CLIENT_ID, handleGoogleCredential, island]);

  // ---------- Phone OTP request ----------
  const handleRequestOtp = useCallback(async () => {
    if (!isValidAzLocal(phoneLocal)) {
      island.show({
        type: 'warn',
        title: 'Düzgün AZ nömrəsi daxil edin',
        message: '+994 50 123 45 67 formatında',
      });
      return;
    }
    setAuthBusy('phone');
    try {
      const phoneE164 = buildAzPhone(phoneLocal);
      const res = await auth.requestPhoneOtp(phoneE164);
      setOtpCooldown(res.cooldown_seconds || 60);
      setOtpExpires(res.expires_in_seconds || 300);
      setOtpDigits(['', '', '', '', '', '']);
      setView('otp');
      island.show({
        type: 'success',
        title: 'Kod göndərildi',
        message: phoneE164,
      });
      // OTP ilk inputuna focus
      requestAnimationFrame(() => {
        otpInputsRef.current[0]?.focus();
      });
    } catch (err: any) {
      island.show({
        type: 'error',
        title:
          err?.response?.data?.message ||
          err?.message ||
          'OTP göndərilmədi',
      });
    } finally {
      setAuthBusy(null);
    }
  }, [phoneLocal, auth, island]);

  // ---------- Phone OTP verify ----------
  const handleVerifyOtp = useCallback(
    async (codeOverride?: string) => {
      const code = codeOverride ?? otpDigits.join('');
      if (code.length !== 6) {
        island.show({
          type: 'warn',
          title: '6 rəqəmli kodu daxil edin',
        });
        return;
      }
      if (needsName && !otpName.trim()) {
        island.show({ type: 'warn', title: 'Adınızı daxil edin' });
        return;
      }
      setAuthBusy('phone');
      try {
        const phoneE164 = buildAzPhone(phoneLocal);
        const customer = await auth.verifyPhoneOtp({
          phoneE164,
          code,
          name: needsName ? otpName.trim() : undefined,
        });
        island.show({
          type: 'success',
          title: customer.name
            ? `Xoş gəldin, ${customer.name}`
            : 'Xoş gəldin',
        });
        setNeedsName(false);
        setOtpName('');
      } catch (err: any) {
        const status = err?.response?.status;
        const msg: string =
          err?.response?.data?.message || err?.message || '';
        // Backend bazen yeni kullanıcı için 422 + name required dönebilir.
        if (
          status === 422 &&
          /name/i.test(msg) &&
          !needsName
        ) {
          setNeedsName(true);
          island.show({
            type: 'info',
            title: 'Adınızı daxil edin',
            message: 'Yeni hesab yaradılır',
          });
          return;
        }
        island.show({
          type: 'error',
          title: msg || 'Kod yanlışdır',
        });
      } finally {
        setAuthBusy(null);
      }
    },
    [otpDigits, otpName, needsName, phoneLocal, auth, island],
  );

  const handleResendOtp = useCallback(async () => {
    if (otpCooldown > 0) return;
    await handleRequestOtp();
  }, [otpCooldown, handleRequestOtp]);

  // ---------- Profile actions ----------
  const handleSaveProfile = useCallback(async () => {
    setEditBusy(true);
    try {
      await auth.updateProfile({
        name: editForm.name?.trim() || undefined,
        email: editForm.email?.trim() || null,
        birth_date: editForm.birth_date?.trim() || null,
      });
      island.show({ type: 'success', title: 'Profil yeniləndi' });
      setView('profile');
    } catch (err: any) {
      island.show({
        type: 'error',
        title:
          err?.response?.data?.message ||
          err?.message ||
          'Profil yenilənmədi',
      });
    } finally {
      setEditBusy(false);
    }
  }, [auth, editForm, island]);

  const handleSignOut = useCallback(async () => {
    try {
      await auth.signOut();
      island.show({ type: 'info', title: 'Çıxış edildi' });
      setView('auth');
    } catch {
      /* ignore — interceptor zaten temizledi */
    }
  }, [auth, island]);

  // ---------- OTP digit input handler ----------
  const handleOtpDigit = useCallback(
    (idx: number, value: string) => {
      const v = value.replace(/\D/g, '').slice(0, 1);
      setOtpDigits((cur) => {
        const next = [...cur];
        next[idx] = v;
        return next;
      });
      // İleri focus
      if (v && idx < 5) {
        otpInputsRef.current[idx + 1]?.focus();
      }
      // Tüm kutular dolduğunda otomatik gönder
      if (v && idx === 5) {
        const code = otpDigits
          .map((d, i) => (i === idx ? v : d))
          .join('');
        if (code.length === 6) {
          // micro delay for state to settle
          setTimeout(() => handleVerifyOtp(code), 80);
        }
      }
    },
    [otpDigits, handleVerifyOtp],
  );

  const handleOtpKeyDown = useCallback(
    (idx: number, e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Backspace' && !otpDigits[idx] && idx > 0) {
        otpInputsRef.current[idx - 1]?.focus();
      } else if (e.key === 'ArrowLeft' && idx > 0) {
        otpInputsRef.current[idx - 1]?.focus();
      } else if (e.key === 'ArrowRight' && idx < 5) {
        otpInputsRef.current[idx + 1]?.focus();
      } else if (e.key === 'Enter') {
        handleVerifyOtp();
      }
    },
    [otpDigits, handleVerifyOtp],
  );

  // OTP paste (yapıştır → 6 rakam)
  const handleOtpPaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      const pasted = (e.clipboardData?.getData('text') || '').replace(/\D/g, '').slice(0, 6);
      if (pasted.length === 0) return;
      e.preventDefault();
      const next = pasted.padEnd(6, '').split('').slice(0, 6);
      while (next.length < 6) next.push('');
      setOtpDigits(next);
      const lastIdx = Math.min(pasted.length - 1, 5);
      otpInputsRef.current[lastIdx]?.focus();
      if (pasted.length === 6) {
        setTimeout(() => handleVerifyOtp(pasted), 80);
      }
    },
    [handleVerifyOtp],
  );

  // Customer initials / display
  const displayName = useMemo(() => {
    if (!auth.customer) return (t as any).guest || 'Qonaq';
    return auth.customer.name || (t as any).guest || 'Qonaq';
  }, [auth.customer, t]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 50 }}
          transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
          className="fixed inset-0 z-[80] bg-[#F8F9FA] dark:bg-[#09090b] qrm-no-select"
        >
          {/* Header gradient */}
          <div className="relative h-[260px] w-full">
            <div className="absolute inset-0 bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600" />
            <img
              src="https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?q=80&w=1200&auto=format&fit=crop"
              alt=""
              className="w-full h-full object-cover mix-blend-overlay opacity-50"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#F8F9FA] dark:from-[#09090b] via-[#F8F9FA]/80 dark:via-[#09090b]/80 to-transparent" />

            {/* Üstte: geri/kapat butonları */}
            <div className="absolute top-6 left-6 right-6 flex items-center justify-between z-10">
              {(view === 'edit' || view === 'ratings') ? (
                <button
                  type="button"
                  onClick={() => setView('profile')}
                  className="w-10 h-10 bg-black/30 backdrop-blur-md rounded-full flex items-center justify-center text-white hover:bg-black/50 transition-colors"
                  aria-label="back"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
              ) : view === 'otp' ? (
                <button
                  type="button"
                  onClick={() => {
                    setView('auth');
                    setNeedsName(false);
                    setOtpName('');
                  }}
                  className="w-10 h-10 bg-black/30 backdrop-blur-md rounded-full flex items-center justify-center text-white hover:bg-black/50 transition-colors"
                  aria-label="back"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
              ) : (
                <span />
              )}
              <button
                type="button"
                onClick={onClose}
                className="w-10 h-10 bg-black/30 backdrop-blur-md rounded-full flex items-center justify-center text-white hover:bg-black/50 transition-colors"
                aria-label="close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Scrollable body */}
          <div className="absolute inset-0 top-0 qrm-modal-scroll scrollbar-hide pt-[150px] pb-12">
            <div className="px-6 relative z-10">
              <AnimatePresence mode="wait">
                {/* ============ AUTH ============ */}
                {view === 'auth' && (
                  <motion.div
                    key="auth"
                    initial={{ y: 16, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: -10, opacity: 0 }}
                    transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                  >
                    <ProfileCardShell>
                      <Avatar name={null} masaName={masaName} t={t} />
                      <h2 className="text-xl font-bold text-gray-900 dark:text-white mt-4 text-center">
                        {(t as any).profileSignInTitle || 'Hesabınıza daxil olun'}
                      </h2>
                      <p className="text-[13px] text-gray-500 dark:text-gray-400 text-center mt-1 max-w-[280px] mx-auto">
                        {(t as any).profileSignInSubtitle ||
                          'Sifariş tarixçəsi, favoritlər və xüsusi təkliflər üçün'}
                      </p>

                      {/* Hidden Google button container (renderButton fallback) */}
                      <div ref={googleBtnRef} className="hidden" aria-hidden="true" />

                      <div className="mt-6 space-y-3">
                        <button
                          type="button"
                          onClick={handleGoogleLogin}
                          disabled={authBusy !== null}
                          className="w-full flex items-center justify-center gap-3 bg-white dark:bg-white/10 border-2 border-gray-200 dark:border-white/10 px-5 py-3.5 rounded-2xl text-[15px] font-bold text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-white/20 transition-all shadow-sm active:scale-[0.98] disabled:opacity-60"
                        >
                          {authBusy === 'google' ? (
                            <Loader2 className="w-5 h-5 animate-spin" />
                          ) : (
                            <svg className="w-5 h-5" viewBox="0 0 24 24">
                              <path
                                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                                fill="#4285F4"
                              />
                              <path
                                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                                fill="#34A853"
                              />
                              <path
                                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                                fill="#FBBC05"
                              />
                              <path
                                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                                fill="#EA4335"
                              />
                            </svg>
                          )}
                          <span>
                            {authBusy === 'google'
                              ? (t as any).connecting || 'Qoşulur…'
                              : (t as any).continueWithGoogle ||
                              'Google ilə davam et'}
                          </span>
                        </button>
                      </div>

                      {/* divider */}
                      <div className="flex items-center gap-3 my-5">
                        <div className="flex-1 h-px bg-gray-200 dark:bg-white/10" />
                        <span className="text-xs font-medium text-gray-400 dark:text-gray-500">
                          {(t as any).or || 'və ya'}
                        </span>
                        <div className="flex-1 h-px bg-gray-200 dark:bg-white/10" />
                      </div>

                      {/* AZ phone input */}
                      <label className="block text-[12px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                        {(t as any).profilePhoneLabel || 'Telefon nömrəsi'}
                      </label>
                      <div className="relative group">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-700 dark:text-gray-200 font-bold pointer-events-none pos-tabular">
                          {AZ_PHONE_PREFIX}
                        </span>
                        <input
                          type="tel"
                          inputMode="numeric"
                          placeholder="50 123 45 67"
                          value={phoneLocal}
                          onChange={(e) => setPhoneLocal(sanitizeAzLocalDigits(e.target.value))}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleRequestOtp();
                          }}
                          className="w-full bg-white/50 dark:bg-black/20 pl-20 pr-14 py-4 rounded-2xl text-[15px] outline-none focus:ring-2 focus:ring-orange-500/50 transition-all font-bold placeholder:text-gray-400 dark:text-white border border-gray-200 dark:border-white/10 shadow-inner pos-tabular"
                          autoComplete="tel-national"
                          maxLength={9}
                        />
                        <button
                          type="button"
                          onClick={handleRequestOtp}
                          disabled={authBusy !== null || !isValidAzLocal(phoneLocal)}
                          className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-orange-500 disabled:bg-gray-300 dark:disabled:bg-white/10 text-white rounded-xl flex items-center justify-center hover:scale-105 active:scale-95 transition-transform shadow-lg shadow-orange-500/30 disabled:shadow-none"
                          aria-label="request otp"
                        >
                          {authBusy === 'phone' ? (
                            <Loader2 className="w-5 h-5 animate-spin" />
                          ) : (
                            <ArrowRight className="w-5 h-5" />
                          )}
                        </button>
                      </div>
                      <p className="mt-2 text-[11px] text-gray-400 dark:text-gray-500 leading-snug">
                        {(t as any).profilePhoneHint ||
                          'Tək istifadəlik 6 rəqəmli kod SMS ilə göndəriləcək.'}
                      </p>
                    </ProfileCardShell>

                    {/* Dark mode toggle — login olmadan da çalışır */}
                    {onToggleTheme && (
                      <div className="mt-5 bg-white/80 dark:bg-[#1a1a1a]/80 backdrop-blur-2xl rounded-[28px] overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.06)] dark:shadow-[0_8px_30px_rgb(0,0,0,0.3)] border border-white/50 dark:border-white/10">
                        <SettingRow
                          icon={theme === 'dark' ? Sun : Moon}
                          label={(t as any).darkMode || 'Qaranlıq mod'}
                          rightSlot={
                            <Switch
                              checked={theme === 'dark'}
                              onChange={() => {
                                haptic(8);
                                onToggleTheme();
                              }}
                            />
                          }
                          onClick={() => {
                            haptic(8);
                            onToggleTheme();
                          }}
                        />
                      </div>
                    )}
                  </motion.div>
                )}

                {/* ============ OTP ============ */}
                {view === 'otp' && (
                  <motion.div
                    key="otp"
                    initial={{ y: 16, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: -10, opacity: 0 }}
                    transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                  >
                    <ProfileCardShell>
                      <div className="flex flex-col items-center">
                        <div className="w-16 h-16 rounded-2xl bg-orange-100 dark:bg-orange-500/20 flex items-center justify-center mb-4">
                          <ShieldCheck className="w-8 h-8 text-orange-600 dark:text-orange-400" />
                        </div>
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white text-center">
                          {(t as any).otpTitle || 'Kodu daxil edin'}
                        </h2>
                        <p className="text-[13px] text-gray-500 dark:text-gray-400 text-center mt-1">
                          {(t as any).otpSentTo || 'Kod göndərildi'}{' '}
                          <span className="font-bold pos-tabular">
                            {buildAzPhone(phoneLocal)}
                          </span>
                        </p>
                      </div>

                      {/* 6-digit grid */}
                      <div className="mt-6 grid grid-cols-6 gap-2">
                        {otpDigits.map((digit, idx) => (
                          <input
                            key={idx}
                            ref={(el) => {
                              if (el) otpInputsRef.current[idx] = el;
                            }}
                            type="tel"
                            inputMode="numeric"
                            maxLength={1}
                            value={digit}
                            onChange={(e) => handleOtpDigit(idx, e.target.value)}
                            onKeyDown={(e) => handleOtpKeyDown(idx, e)}
                            onPaste={idx === 0 ? handleOtpPaste : undefined}
                            className="aspect-square w-full text-center text-xl font-black text-gray-900 dark:text-white bg-white dark:bg-white/5 border-2 border-gray-200 dark:border-white/10 rounded-xl outline-none focus:border-orange-500 focus:bg-orange-50/50 dark:focus:bg-orange-500/10 transition-all pos-tabular"
                            aria-label={`OTP digit ${idx + 1}`}
                          />
                        ))}
                      </div>

                      {/* OTP expires + resend */}
                      <div className="mt-3 flex items-center justify-between text-[12px]">
                        <span className="text-gray-500 dark:text-gray-400">
                          {otpExpires > 0 ? (
                            <>
                              {(t as any).otpExpiresIn || 'Bitir'}{' '}
                              <span className="font-bold pos-tabular">
                                {formatTime(otpExpires)}
                              </span>
                            </>
                          ) : (
                            <span className="text-red-500 font-bold">
                              {(t as any).otpExpired || 'Kod müddəti bitdi'}
                            </span>
                          )}
                        </span>
                        <button
                          type="button"
                          onClick={handleResendOtp}
                          disabled={otpCooldown > 0 || authBusy === 'phone'}
                          className="text-orange-600 dark:text-orange-400 font-bold disabled:text-gray-400 dark:disabled:text-gray-500 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
                        >
                          {otpCooldown > 0 ? (
                            <span className="pos-tabular">
                              {(t as any).otpResendIn || 'Yenidən'}{' '}
                              {otpCooldown}s
                            </span>
                          ) : (
                            <>
                              <RefreshCw className="w-3.5 h-3.5" />
                              {(t as any).otpResend || 'Yenidən göndər'}
                            </>
                          )}
                        </button>
                      </div>

                      {/* New user → name input */}
                      {needsName && (
                        <div className="mt-5">
                          <label className="block text-[12px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                            {(t as any).profileNameLabel || 'Adınız'}
                          </label>
                          <input
                            type="text"
                            placeholder={(t as any).profileNamePlaceholder || 'Adınız'}
                            value={otpName}
                            onChange={(e) => setOtpName(e.target.value)}
                            className="w-full bg-white/50 dark:bg-black/20 px-4 py-3.5 rounded-2xl text-[15px] outline-none focus:ring-2 focus:ring-orange-500/50 transition-all font-bold placeholder:text-gray-400 dark:text-white border border-gray-200 dark:border-white/10 shadow-inner"
                            autoFocus
                          />
                        </div>
                      )}

                      <button
                        type="button"
                        onClick={() => handleVerifyOtp()}
                        disabled={
                          authBusy === 'phone' ||
                          otpDigits.join('').length !== 6 ||
                          (needsName && !otpName.trim())
                        }
                        className="w-full mt-5 flex items-center justify-center gap-2 bg-orange-500 disabled:bg-gray-300 dark:disabled:bg-white/10 text-white px-5 py-4 rounded-2xl text-[15px] font-bold shadow-lg shadow-orange-500/30 disabled:shadow-none active:scale-[0.98] transition-all"
                      >
                        {authBusy === 'phone' ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <CheckCircle2 className="w-5 h-5" />
                        )}
                        {(t as any).otpVerify || 'Davam et'}
                      </button>
                    </ProfileCardShell>
                  </motion.div>
                )}

                {/* ============ PROFILE ============ */}
                {view === 'profile' && auth.customer && (
                  <motion.div
                    key="profile"
                    initial={{ y: 16, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: -10, opacity: 0 }}
                    transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                  >
                    {/* User card */}
                    <ProfileCardShell>
                      <Avatar
                        name={auth.customer.name}
                        avatarUrl={auth.customer.avatar}
                        masaName={masaName}
                        t={t}
                      />
                      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-4 text-center tracking-tight">
                        {displayName}
                      </h2>
                      {(auth.customer.email || auth.customer.phone) && (
                        <div className="mt-2 flex flex-col items-center gap-1">
                          {auth.customer.email && (
                            <p className="inline-flex items-center gap-2 text-[12px] text-gray-500 dark:text-gray-400 font-medium truncate max-w-[280px]">
                              <Mail className="w-3.5 h-3.5" />
                              {auth.customer.email}
                            </p>
                          )}
                          {auth.customer.phone && (
                            <p className="inline-flex items-center gap-2 text-[12px] text-gray-500 dark:text-gray-400 font-medium pos-tabular">
                              <Phone className="w-3.5 h-3.5" />
                              {auth.customer.phone}
                            </p>
                          )}
                        </div>
                      )}
                      {masaName && (
                        <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-100/80 dark:bg-orange-500/15 text-orange-700 dark:text-orange-300 text-[11px] font-bold mx-auto self-center">
                          <span className="w-1.5 h-1.5 rounded-full bg-orange-500 animate-pulse" />
                          {(t as any).table_short || 'Masa'} {masaName}
                        </div>
                      )}

                      <button
                        type="button"
                        onClick={() => setView('edit')}
                        className="mt-5 w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-2xl bg-gray-100 dark:bg-white/10 text-gray-800 dark:text-gray-200 text-sm font-bold hover:bg-gray-200 dark:hover:bg-white/15 active:scale-[0.98] transition-all"
                      >
                        <Pencil className="w-4 h-4" />
                        {(t as any).profileEdit || 'Profili düzənlə'}
                      </button>
                    </ProfileCardShell>

                    {/* Rezmo Puan Card */}
                    <div className="relative mt-5 bg-[#1e2030] dark:bg-white backdrop-blur-2xl rounded-[28px] p-5 shadow-[0_8px_30px_rgb(0,0,0,0.15)] dark:shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-white/10 dark:border-gray-200 overflow-hidden">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Star className="w-5 h-5 text-yellow-400 fill-yellow-400 dark:text-yellow-500 dark:fill-yellow-500" />
                          <span className="text-sm font-black text-white dark:text-gray-900">
                            Rezmo Puan
                          </span>
                        </div>
                        <div className="w-8 h-8 rounded-xl bg-white/10 dark:bg-gray-100 flex items-center justify-center">
                          <Gift className="w-4 h-4 text-gray-300 dark:text-gray-500" />
                        </div>
                      </div>
                      <div className="flex items-baseline gap-2 mb-1">
                        <span className="text-3xl font-black text-white dark:text-gray-900 tracking-tight">
                          1,250
                        </span>
                        <span className="text-sm font-bold text-gray-400 dark:text-gray-500">
                          {(t as any).points || 'puan'}
                        </span>
                      </div>
                      {/* Star decoration */}
                      <div className="absolute right-6 top-1/2 -translate-y-1/2 opacity-10 dark:opacity-[0.06] pointer-events-none">
                        <Star className="w-24 h-24 text-white dark:text-gray-400" />
                      </div>
                      {/* Progress bar */}
                      <div className="w-full h-2 bg-white/10 dark:bg-gray-200 rounded-full overflow-hidden mt-3 mb-2">
                        <div
                          className="h-full rounded-full bg-gradient-to-r from-pink-500 via-rose-400 to-orange-400"
                          style={{ width: '83%' }}
                        />
                      </div>
                      <p className="text-[11px] font-semibold text-gray-400 dark:text-gray-500">
                        {(t as any).rezmoPuanHint || 'Sıradaki kahveniz bizden! 250 puan daha kazanın.'}
                      </p>
                    </div>

                    {/* Settings group */}
                    <div className="mt-5 bg-white/80 dark:bg-[#1a1a1a]/80 backdrop-blur-2xl rounded-[28px] overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.06)] dark:shadow-[0_8px_30px_rgb(0,0,0,0.3)] border border-white/50 dark:border-white/10">
                      {onToggleTheme && (
                        <SettingRow
                          icon={theme === 'dark' ? Sun : Moon}
                          label={(t as any).darkMode || 'Qaranlıq mod'}
                          rightSlot={
                            <Switch
                              checked={theme === 'dark'}
                              onChange={() => {
                                haptic(8);
                                onToggleTheme();
                              }}
                            />
                          }
                          onClick={() => {
                            haptic(8);
                            onToggleTheme();
                          }}
                        />
                      )}
                      <SettingRow
                        icon={Heart}
                        label={
                          (t as any).favoritesShowCategory ||
                          'Favorit kategoriyası'
                        }
                        rightSlot={
                          <Switch
                            checked={favorites.showFavoritesCategory}
                            onChange={(v) => {
                              haptic(8);
                              favorites.setShowFavoritesCategory(v);
                              island.show({
                                type: 'info',
                                title: v
                                  ? (t as any).favoritesShown ||
                                  'Favorit kategoriyası göstərilir'
                                  : (t as any).favoritesHidden ||
                                  'Favorit kategoriyası gizlədildi',
                              });
                            }}
                          />
                        }
                      />
                      <SettingRow
                        icon={Star}
                        label={
                          (t as any).profileMyRatings ||
                          'Verdiyim qiymətlər'
                        }
                        rightSlot={
                          <ChevronRight className="w-4 h-4 text-gray-400" />
                        }
                        onClick={() => setView('ratings')}
                      />
                    </div>

                    {/* Logout */}
                    <button
                      type="button"
                      onClick={handleSignOut}
                      disabled={auth.pending}
                      className="mt-5 w-full inline-flex items-center justify-center gap-2 px-4 py-3.5 rounded-2xl bg-red-500/10 hover:bg-red-500/15 text-red-600 dark:text-red-400 text-sm font-bold border border-red-500/20 active:scale-[0.98] transition-all disabled:opacity-60"
                    >
                      {auth.pending ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <LogOut className="w-4 h-4" />
                      )}
                      {(t as any).signOut || 'Çıxış'}
                    </button>
                  </motion.div>
                )}

                {/* ============ EDIT ============ */}
                {view === 'edit' && auth.customer && (
                  <motion.div
                    key="edit"
                    initial={{ y: 16, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: -10, opacity: 0 }}
                    transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                  >
                    <ProfileCardShell>
                      <h2 className="text-xl font-bold text-gray-900 dark:text-white text-center">
                        {(t as any).profileEditTitle || 'Profili düzənlə'}
                      </h2>
                      <p className="text-[13px] text-gray-500 dark:text-gray-400 text-center mt-1">
                        {(t as any).profileEditSubtitle ||
                          'Telefon və Google ID dəyişdirilmir'}
                      </p>

                      <div className="mt-5 space-y-4">
                        <FormField
                          label={(t as any).profileNameLabel || 'Adınız'}
                          icon={User}
                        >
                          <input
                            type="text"
                            value={editForm.name}
                            onChange={(e) =>
                              setEditForm((f) => ({ ...f, name: e.target.value }))
                            }
                            placeholder={
                              (t as any).profileNamePlaceholder || 'Adınız'
                            }
                            className="w-full bg-transparent outline-none text-[15px] font-bold text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500"
                          />
                        </FormField>
                        <FormField
                          label={(t as any).profileEmailLabel || 'E-poçt'}
                          icon={Mail}
                        >
                          <input
                            type="email"
                            value={editForm.email}
                            onChange={(e) =>
                              setEditForm((f) => ({ ...f, email: e.target.value }))
                            }
                            placeholder="ad@example.com"
                            className="w-full bg-transparent outline-none text-[15px] font-bold text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500"
                          />
                        </FormField>
                        <FormField
                          label={
                            (t as any).profileBirthLabel || 'Doğum tarixi'
                          }
                          icon={Calendar}
                        >
                          <input
                            type="date"
                            value={editForm.birth_date}
                            onChange={(e) =>
                              setEditForm((f) => ({
                                ...f,
                                birth_date: e.target.value,
                              }))
                            }
                            className="w-full bg-transparent outline-none text-[15px] font-bold text-gray-900 dark:text-white pos-tabular"
                          />
                        </FormField>
                      </div>

                      <button
                        type="button"
                        onClick={handleSaveProfile}
                        disabled={editBusy}
                        className="w-full mt-5 flex items-center justify-center gap-2 bg-orange-500 text-white px-5 py-4 rounded-2xl text-[15px] font-bold shadow-lg shadow-orange-500/30 active:scale-[0.98] transition-all disabled:opacity-60"
                      >
                        {editBusy ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <CheckCircle2 className="w-5 h-5" />
                        )}
                        {(t as any).save || 'Yadda saxla'}
                      </button>
                    </ProfileCardShell>
                  </motion.div>
                )}

                {/* ============ MY RATINGS ============ */}
                {view === 'ratings' && (
                  <motion.div
                    key="ratings"
                    initial={{ y: 16, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: -10, opacity: 0 }}
                    transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                  >
                    <ProfileCardShell>
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-2xl bg-yellow-100 dark:bg-yellow-500/20 flex items-center justify-center">
                          <Star className="w-5 h-5 text-yellow-600 dark:text-yellow-400 fill-yellow-500" />
                        </div>
                        <div>
                          <h2 className="text-lg font-bold text-gray-900 dark:text-white leading-tight">
                            {(t as any).profileMyRatings || 'Verdiyim qiymətlər'}
                          </h2>
                          <p className="text-[12px] text-gray-500 dark:text-gray-400">
                            {ratings.length}{' '}
                            {(t as any).profileRatingsCount || 'qiymət'}
                          </p>
                        </div>
                      </div>

                      {ratingsLoading && (
                        <div className="py-10 flex items-center justify-center">
                          <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
                        </div>
                      )}

                      {!ratingsLoading && ratings.length === 0 && (
                        <div className="py-10 text-center">
                          <div className="w-16 h-16 mx-auto mb-3 rounded-2xl bg-gray-100 dark:bg-white/5 flex items-center justify-center">
                            <Star className="w-8 h-8 text-gray-300 dark:text-gray-600" />
                          </div>
                          <p className="text-sm font-bold text-gray-700 dark:text-gray-300">
                            {(t as any).profileNoRatings ||
                              'Hələ qiymət verməmisiniz'}
                          </p>
                          <p className="text-[12px] text-gray-500 dark:text-gray-400 mt-1">
                            {(t as any).profileNoRatingsDesc ||
                              'Restorana və ofisanta qiymət verə bilərsiniz.'}
                          </p>
                        </div>
                      )}

                      {!ratingsLoading && ratings.length > 0 && (
                        <div className="space-y-3">
                          {ratings.map((r) => (
                            <RatingItem key={`${r.type}-${r.id}`} rating={r} />
                          ))}
                        </div>
                      )}
                    </ProfileCardShell>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ---------------------------------------------------------------------------
// Helper sub-components
// ---------------------------------------------------------------------------

const ProfileCardShell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="bg-white/80 dark:bg-[#1a1a1a]/80 backdrop-blur-2xl rounded-[32px] p-6 shadow-[0_8px_30px_rgb(0,0,0,0.06)] dark:shadow-[0_8px_30px_rgb(0,0,0,0.3)] border border-white/50 dark:border-white/10">
    {children}
  </div>
);

const Avatar: React.FC<{
  name?: string | null;
  avatarUrl?: string | null;
  masaName?: string;
  t: any;
}> = ({ name, avatarUrl }) => (
  <div className="w-24 h-24 rounded-full bg-gradient-to-tr from-orange-400 to-pink-500 p-1 mx-auto shadow-xl">
    <div className="w-full h-full rounded-full bg-white dark:bg-[#09090b] flex items-center justify-center overflow-hidden">
      {avatarUrl ? (
        <img
          src={avatarUrl}
          alt={name || ''}
          className="w-full h-full object-cover"
          referrerPolicy="no-referrer"
        />
      ) : name ? (
        <span className="text-3xl font-black text-orange-500">
          {initialOf(name)}
        </span>
      ) : (
        <User className="w-10 h-10 text-gray-400" />
      )}
    </div>
  </div>
);

const SettingRow: React.FC<{
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  rightSlot?: React.ReactNode;
  onClick?: () => void;
}> = ({ icon: Icon, label, rightSlot, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="w-full flex items-center justify-between p-5 hover:bg-white dark:hover:bg-white/10 transition-colors border-b border-gray-100 dark:border-white/5 last:border-b-0 group"
  >
    <div className="flex items-center gap-4">
      <div className="w-10 h-10 rounded-xl bg-gray-100 dark:bg-white/5 flex items-center justify-center group-hover:bg-gray-200 dark:group-hover:bg-white/10 transition-colors">
        <Icon className="w-5 h-5 text-gray-600 dark:text-gray-300" />
      </div>
      <span className="font-bold text-[15px] text-gray-900 dark:text-white">
        {label}
      </span>
    </div>
    {rightSlot}
  </button>
);

const Switch: React.FC<{
  checked: boolean;
  onChange: (next: boolean) => void;
}> = ({ checked, onChange }) => (
  <span
    onClick={(e) => {
      e.stopPropagation();
      onChange(!checked);
    }}
    className={`w-12 h-6 rounded-full transition-colors flex items-center px-1 cursor-pointer ${checked ? 'bg-emerald-500' : 'bg-gray-300 dark:bg-white/15'
      }`}
  >
    <motion.span
      layout
      className="w-4 h-4 rounded-full bg-white shadow-sm"
      initial={false}
      animate={{ x: checked ? 24 : 0 }}
      transition={{ type: 'spring', stiffness: 500, damping: 30 }}
    />
  </span>
);

const FormField: React.FC<{
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
}> = ({ label, icon: Icon, children }) => (
  <div>
    <label className="block text-[12px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
      {label}
    </label>
    <div className="flex items-center gap-3 px-4 py-3 rounded-2xl bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 focus-within:border-orange-400 transition-colors">
      <Icon className="w-4 h-4 text-gray-400 dark:text-gray-500 shrink-0" />
      {children}
    </div>
  </div>
);

const RatingItem: React.FC<{ rating: CustomerRating }> = ({ rating }) => {
  const target =
    rating.target ||
    rating.restaurant ||
    rating.waiter ||
    null;
  const isWaiter = rating.type === 'waiter' || !!rating.waiter;
  const targetName =
    target?.name ||
    (isWaiter ? 'Ofisant' : 'Restoran');
  const date = rating.created_at
    ? new Date(rating.created_at).toLocaleDateString('az-AZ', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    })
    : '';
  return (
    <div className="p-3 rounded-2xl bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/5">
      <div className="flex items-start gap-3">
        <div
          className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${isWaiter
            ? 'bg-indigo-100 dark:bg-indigo-500/20 text-indigo-600 dark:text-indigo-400'
            : 'bg-orange-100 dark:bg-orange-500/20 text-orange-600 dark:text-orange-400'
            }`}
        >
          {isWaiter ? <User className="w-5 h-5" /> : <Star className="w-5 h-5 fill-current" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm font-bold text-gray-900 dark:text-white truncate">
              {targetName}
            </p>
            <div className="flex items-center gap-0.5 shrink-0">
              {[1, 2, 3, 4, 5].map((n) => (
                <Star
                  key={n}
                  className={`w-3 h-3 ${n <= rating.rating
                    ? 'fill-yellow-500 text-yellow-500'
                    : 'text-gray-300 dark:text-gray-600'
                    }`}
                />
              ))}
            </div>
          </div>
          {rating.comment && (
            <p className="text-[12px] text-gray-600 dark:text-gray-400 mt-1 leading-snug">
              {rating.comment}
            </p>
          )}
          {date && (
            <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-1 pos-tabular">
              {date}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
